import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { browseTags, type Tag } from "../api";
import { useT } from "../i18n";

const chip = (on: boolean) =>
  `shrink-0 rounded-full border px-3 py-1 text-sm transition-colors ${
    on
      ? "border-sage bg-sage-soft font-medium text-sage-deep"
      : "border-line text-muted hover:text-ink"
  }`;

/** The address of Browse narrowed to a tag. */
export const browseTagPath = (id: string) => `/browse?tag=${encodeURIComponent(id)}`;

/**
 * BrowseTagChips is Browse's row of tags (requirement 0119 T3): pill chips, no
 * dropdown on a phone. The server sends only tags on a book this viewer may
 * read in Browse, so a chip never leads to an empty grid; with none, or on a
 * failure, the row is not there. A tag is authored text, so each chip carries
 * `dir="auto"` and nothing inside it does.
 *
 * The chosen chip is kept in view, as far as it takes and no further: a tag
 * opened from a book page may be far along the row. Only the row scrolls;
 * `scrollIntoView` would move the page too.
 */
export function BrowseTagChips({
  selected,
  onSelect,
}: {
  selected: string | null;
  onSelect: (id: string | null) => void;
}) {
  const t = useT();
  const { data } = useQuery({ queryKey: ["tags", "browse"], queryFn: browseTags });
  const tags = data?.items ?? [];
  const row = useRef<HTMLElement>(null);
  useEffect(() => {
    const nav = row.current;
    const chip = selected && nav?.querySelector(`[data-tag="${CSS.escape(selected)}"]`);
    if (!nav || !chip) return;
    const outer = nav.getBoundingClientRect();
    const inner = chip.getBoundingClientRect();
    const by =
      inner.left < outer.left
        ? inner.left - outer.left
        : inner.right > outer.right
          ? inner.right - outer.right
          : 0;
    if (by !== 0) nav.scrollBy({ left: by });
  }, [selected, tags.length]);
  if (tags.length === 0) return null;
  return (
    <nav
      ref={row}
      aria-label={t.tag.filter}
      data-testid="tag-chips"
      className="-mx-4 mb-4 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none]"
    >
      <button
        type="button"
        data-testid="tag-chip-all"
        aria-pressed={selected === null}
        onClick={() => onSelect(null)}
        className={chip(selected === null)}
      >
        {t.tag.all}
      </button>
      {tags.map((tag) => (
        <button
          key={tag.id}
          type="button"
          dir="auto"
          data-testid="tag-chip"
          data-tag={tag.id}
          aria-pressed={selected === tag.id}
          onClick={() => onSelect(selected === tag.id ? null : tag.id)}
          className={chip(selected === tag.id)}
        >
          {tag.name}
        </button>
      ))}
    </nav>
  );
}

/**
 * BookTags is a book's tags on its page, below the chapters: a small muted
 * label and one row of compact chips that scrolls sideways, each opening
 * Browse narrowed to it. Out of the header, where every tag listed under the
 * cover "takes too much space" (owner, 2026-10-19).
 */
export function BookTags({ tags, className = "" }: { tags?: Tag[]; className?: string }) {
  const t = useT();
  if (!tags || tags.length === 0) return null;
  return (
    <section aria-label={t.tag.tags} data-testid="book-tags" className={className}>
      <h2 className="text-xs font-medium text-muted">{t.tag.tags}</h2>
      <ul className="-mx-4 mt-1.5 flex gap-1.5 overflow-x-auto px-4 pb-1 [scrollbar-width:none]">
        {tags.map((tag) => (
          <li key={tag.id} className="shrink-0">
            <Link
              to={browseTagPath(tag.id)}
              dir="auto"
              data-testid="book-tag"
              className="inline-block rounded-full border border-line px-2 py-0.5 text-[0.7rem] whitespace-nowrap text-muted hover:border-sage hover:text-ink"
            >
              {tag.name}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
